import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Link,
} from "@mui/material";
import { Rnd } from "react-rnd";
import { findSimilarPaintings } from "../services/similarityService";
import { getPaintings } from "../services/api";
import { motion } from "framer-motion";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/Email";
import { Link as RouterLink } from "react-router-dom";

// --- THEME COLORS ---
const CHARCOAL = "#18191b";
const CHARCOAL_LIGHT = "#232526";
const GOLD = "#FFD700";
const TURQUOISE = "#008080";
const BURGUNDY = "#800020";
const TEXT_COLOR_LIGHT = "#F0F0F0";
const Serif = "'Playfair Display', Georgia, serif";
const Sans = "'Montserrat', Arial, sans-serif";

const ResultsPage = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [error, setError] = useState(null);
  const [selectedPainting, setSelectedPainting] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [frameThickness, setFrameThickness] = useState(2);
  const location = useLocation();
  const { embedding, uploadedImage } = location.state || {};

  // For tracking Rnd size
  const [rndSize, setRndSize] = useState({ width: 180, height: 180 });

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!embedding) {
        console.log("No embedding provided");
        return;
      }
      try {
        const paintings = await getPaintings();
        const similarPaintings = findSimilarPaintings(embedding, paintings);
        setRecommendations(similarPaintings.slice(0, 9));
      } catch (error) {
        console.error("Error fetching recommendations:", error);
        setError(error);
      }
    };
    fetchRecommendations();
  }, [embedding]);

  const handleSelectPainting = (painting) => {
    setSelectedPainting(painting);
    setShowOverlay(true);
    setRndSize({ width: 180, height: 180 });
    setFrameThickness(2);
  };

  // Stripe Checkout
  const handleBuy = async (paintingId) => {
    setLoadingCheckout(paintingId);
    try {
      const response = await fetch("/api/create-checkout-session/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paintingId }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Failed to create checkout session. Please try again.");
      }
    } catch (err) {
      alert("Error connecting to payment gateway.");
    }
    setLoadingCheckout(null);
  };

  // For painting click
  const handlePaintingClick = (painting) => {
    handleSelectPainting(painting);
  };

  // Adjust frame thickness based on size
  const handleRndResize = (e, direction, ref, delta, position) => {
    const width = parseInt(ref.style.width, 10);
    const height = parseInt(ref.style.height, 10);
    setRndSize({ width, height });
    // Frame thickness: min 2px, max 10px, proportional to size
    const minDim = Math.min(width, height);
    const thickness = Math.max(2, Math.min(10, Math.round(minDim / 40)));
    setFrameThickness(thickness);
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #008080 0%, #00b3b3 100%)",
        py: { xs: 4, md: 10 },
        fontFamily: "'Inter', 'Georgia', serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Artistic background shapes */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          left: -120,
          width: 340,
          height: 340,
          background: "radial-gradient(circle at 60% 40%, #FFD70033 0%, transparent 80%)",
          zIndex: 0,
          filter: "blur(2px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -100,
          right: -100,
          width: 260,
          height: 260,
          background: "radial-gradient(circle at 40% 60%, #80002033 0%, transparent 80%)",
          zIndex: 0,
          filter: "blur(2px)",
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          minHeight: "100vh",
          py: 6,
          zIndex: 1,
          position: "relative",
        }}
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        {/* Uploaded Image Section */}
        <Box sx={{ mt: 4, mb: 4, textAlign: "center", position: "relative" }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: "#FFD700",
              fontWeight: 700,
              fontFamily: "Georgia, serif",
              letterSpacing: 2,
              textShadow: "1px 1px 0 #80002044",
            }}
          >
            Your Room Image
          </Typography>
          <Paper
            elevation={3}
            sx={{
              display: "inline-block",
              borderRadius: 8,
              border: "3px solid #18191b",
              background: "#111",
              position: "relative",
              p: 2,
              backgroundClip: "padding-box",
              minWidth: { xs: 260, sm: 340, md: 420 },
              maxWidth: 600,
            }}
          >
            <img
              src={uploadedImage}
              alt="Uploaded Room"
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                borderRadius: 8,
                background: "none",
              }}
            />
            {/* Overlay painting */}
            {showOverlay && selectedPainting && (
              <Rnd
                size={rndSize}
                default={{
                  x: 100,
                  y: 100,
                  width: 180,
                  height: 180,
                }}
                bounds="parent"
                minWidth={50}
                minHeight={50}
                style={{
                  zIndex: 10,
                  border: `${frameThickness}px solid #18191b`,
                  borderRadius: 0,
                  cursor: "move",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "none",
                  boxSizing: "border-box",
                  transition: "border-width 0.2s",
                }}
                onResize={handleRndResize}
                onResizeStop={handleRndResize}
              >
                <img
                  src={selectedPainting.image_url}
                  alt={selectedPainting.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: 0,
                    pointerEvents: "none",
                    background: "#232526",
                  }}
                  draggable={false}
                />
              </Rnd>
            )}
          </Paper>
          {showOverlay && (
            <Typography sx={{ mt: 2, color: "#800020", fontWeight: "500" }}>
              Drag and resize the painting to preview it on your wall.
            </Typography>
          )}
        </Box>

        {/* Recommendations Section */}
        <Typography
          variant="h4"
          sx={{
            mb: 3,
            fontWeight: 700,
            color: "#FFD700",
            fontFamily: "Georgia, serif",
            letterSpacing: 2,
            textShadow: "1px 1px 0 #80002044",
          }}
        >
          <span style={{ color: "#800020" }}>Click on a painting to</span>{" "}
          <span style={{ color: "#FFD700" }}>see how it looks on your image of the wall!</span>
        </Typography>
        <Grid container spacing={4}>
          {recommendations.map((painting, idx) => (
            <Grid item xs={12} sm={6} md={4} key={painting.id}>
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.12,
                  type: "spring",
                  stiffness: 120,
                }}
                whileHover={{
                  scale: 1.04,
                  boxShadow: "0 8px 32px #80002033",
                  transition: { duration: 0.2 },
                }}
              >
                <Card
                  sx={{
                    borderRadius: 6,
                    border: "2.5px solid #FFD700",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.10)",
                    background: "none",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      borderColor: "#800020",
                    },
                  }}
                  onClick={() => handlePaintingClick(painting)}
                >
                  <CardMedia
                    component="img"
                    image={painting.image_url}
                    alt={painting.title}
                    sx={{
                      width: "100%",
                      height: 320,
                      borderTopRightRadius: 6,
                      borderTopLeftRadius: 6,
                      objectFit: "cover",
                      background: "none",
                      transition: "box-shadow 0.2s",
                    }}
                  />
                  <CardContent sx={{ width: "100%" }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#800020", fontFamily: "Georgia, serif", letterSpacing: 1, }}>
                      {painting.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#008080", fontWeight: 600
                      }}>
                      {painting.artist}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "#FFD700", fontWeight: 700, mt: 1, fontSize: 18 }}
                    >
                      {painting.price}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        mt: 2,
                        background: "linear-gradient(90deg, #FFD700 0%, #800020 100%)",
                        color: "#008080",
                        fontWeight: "bold",
                        borderRadius: 3,
                        letterSpacing: 1,
                        boxShadow: "0 2px 8px #FFD70033",
                        "&:hover": {
                          background: "linear-gradient(90deg, #800020 0%, #FFD700 100%)",
                          color: "#FFD700",
                        },
                        width: "100%",
                        fontSize: 16,
                        py: 1,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleBuy(painting.id);
                      }}
                      disabled={loadingCheckout === painting.id}
                    >
                      {loadingCheckout === painting.id ? (
                        <CircularProgress size={22} sx={{ color: "#FFD700" }} />
                      ) : (
                        "Buy"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FOOTER */}
      <Box sx={{ background: CHARCOAL, color: CHARCOAL_LIGHT, py: 4, textAlign: "center", mt: 8 }}>
        <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>
          &copy; {new Date().getFullYear()} Art For My Room. All rights reserved.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Link href="https://instagram.com/arttantalizing" target="_blank" rel="noopener" sx={{ color: GOLD, '&:hover': { color: TEXT_COLOR_LIGHT } }}><InstagramIcon /></Link>
          <Link href="mailto:info@artformyroom.com" sx={{ color: GOLD, '&:hover': { color: TEXT_COLOR_LIGHT } }}><EmailIcon /></Link>
          <Link component={RouterLink} to="/privacy" sx={{ color: GOLD, '&:hover': { color: TEXT_COLOR_LIGHT }, textDecoration: 'none', fontFamily: Sans, fontSize: '0.9rem' }}>Privacy Policy</Link>
        </Box>
      </Box>
    </Box>
  );
};

export default ResultsPage;
